import { Option, ReadonlyArray, pipe } from "@morphism/fp";
import { DatePicker } from "antd";
import * as Moment from "moment";
import React from "react";

import { DVIR, DVIRs } from "~/DVIR";
import { Order } from "~/Order";
import { Time } from "~/Time";

export function Date({ dvir }: { readonly dvir: DVIR }) {
  return <>{Time.DateTime.toDateOnly(dvir.lifecycle.submitted)}</>;
}

export namespace Date {
  export const column = ({
    interval,
    onChange,
  }: Partial<Time.Interval.Stopped> & {
    readonly onChange: (interval?: Time.Interval.Stopped) => void;
  }) => ({
    title: "Date",
    dataIndex: "dateTime",
    defaultSortOrder: "descend" as const,

    filteredValue: interval && ["dateTime"],
    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter onReset={onChange} {...{ interval, onChange }} {...props} />
    ),
    onFilter: (_: unknown, dvir: DVIR) =>
      pipe(dvir, getFilterFunc({ interval })),
    render: (_: unknown, dvir: DVIR) => <Date dvir={dvir} />,
    sorter: (a: DVIR, b: DVIR): Order.Ordering =>
      Time.DateTime.compare(a.lifecycle.submitted, b.lifecycle.submitted),
  });

  // Generic filter function to apply for CSV export
  export const getFilterFunc = ({
    interval,
  }: Partial<Time.Interval.Stopped>) => (dvir: DVIR) =>
    pipe(
      Option.fromNullable(interval && { interval }),
      Option.map(Time.Interval.Stopped.contains(dvir.lifecycle.submitted)),
      Option.getOrElse(() => true)
    );

  // Build same filter function ^ based on current table "settings" (filters)
  export const getFilterFuncForSettings = (settings: DVIRs.Table.Settings) =>
    getFilterFunc(settings);

  export const Filter = ({
    interval,
    onChange,
    confirm,
  }: DVIRs.Table.Filter.Props & Parameters<typeof column>[0]) => (
    <DVIRs.Table.Filter confirm={confirm} onReset={interval && onChange}>
      <DatePicker.RangePicker
        format="MM/DD/YYYY"
        bordered={false}
        allowClear={false}
        allowEmpty={[false, false]}
        defaultValue={
          pipe(
            interval ?? DVIRs.Interval.use()[0].interval,
            ({ start, stop }) => [start, stop],
            ReadonlyArray.map(({ dateTime }) => Moment.default(dateTime))
          ) as never
        }
        onChange={(moments) =>
          moments?.[0] &&
          moments[1] &&
          pipe(
            moments,
            ReadonlyArray.map((moment) =>
              Time.DateTime.fromNumber(
                Moment.default(moment).startOf("day").valueOf()
              )
            ),
            ([start, stop]) =>
              onChange(Time.Interval.Stopped.fromDateTimes(start, stop))
          )
        }
      />
    </DVIRs.Table.Filter>
  );
}
